import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { AuthService } from './services/auth.service';
import { CommonModule } from '@angular/common';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { NgZone } from '@angular/core';
import { AnnouncementService } from './services/announcement.service';
import { ReleaseNotesComponent } from './shared/components/release-notes/release-notes.component';
import { SettingsComponent } from './settings/settings.component';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';

// --- Announcement Modal Config ---
const ANNOUNCEMENT_VERSION = '2025-05-06-release'; // Change this string to reset for all users
const ANNOUNCEMENT_HTML = `
  <h2>🚀 Portal Update: May 2025</h2>
  <p>Welcome to the new release!<br>
  <ul>
    <li>New dynamic page titles</li>
    <li>Export improvements</li>
    <li>Timezone-aware display</li>
    <li>And more!</li>
  </ul>
  <p>Contact support if you have any questions.</p>
`;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    HeaderComponent,
    FooterComponent,
    CommonModule,
    ReleaseNotesComponent,
    SettingsComponent,
  ],
})
export class AppComponent implements OnInit {
  showAnnouncement = false;
  showUpdateBanner = false;
  latestVersion = '';
  // Modal should only be triggered by the footer link. Do NOT auto-show on load.
  // Remove any logic that pops up the modal automatically.
  announcementHtml = ANNOUNCEMENT_HTML;

  constructor(
    public auth: AuthService,
    private firestore: Firestore,
    private ngZone: NgZone,
    private announcementService: AnnouncementService,
    private router: Router
  ) {
    // Listen for openModal events from AnnouncementService (footer link)
    this.announcementService.openModal$.subscribe(() => {
      console.debug('AppComponent: openModal$ received, opening modal');
      this.showAnnouncement = true;
    });

    // Listen for login and show modal if flagged
    this.auth.user$.subscribe((user) => {
      if (user && localStorage.getItem('showReleaseNotesAfterLogin') === '1') {
        this.announcementService.openModal();
        localStorage.removeItem('showReleaseNotesAfterLogin');
      }
    });
  }

  isCallbackRoute(): boolean {
    // Returns true if current route is /auth or /quickbooks-callback
    const url = this.router.url;
    return url.startsWith('/auth') || url.startsWith('/quickbooks-callback');
  }

  isLoginPage(): boolean {
    // Handles both root ('/'), '/login', '/landing', and callback routes as non-login pages
    const url = this.router.url;
    return (
      url === '/' ||
      url.startsWith('/login') ||
      url.startsWith('/landing') ||
      this.isCallbackRoute()
    );
  }

  async onAcknowledgeAnnouncement() {
    try {
      const user = await firstValueFrom(this.auth.user$);
      if (user && user.email) {
        const userDocRef = doc(this.firestore, 'users', user.email);
        await setDoc(
          userDocRef,
          { announcementSeen: ANNOUNCEMENT_VERSION },
          { merge: true }
        );
      }
    } catch (err) {
      // Optionally log error
    }
    // Always update localStorage for anonymous users as well
    localStorage.setItem('announcementSeen', ANNOUNCEMENT_VERSION);
    this.ngZone.run(() => {
      this.showAnnouncement = false;
    });
  }

  reloadPage() {
    window.location.reload();
  }

  ngOnInit(): void {
    /// PROJECT HUDDLE CAN EAT SHIT
    // const k = 'ph_access_token';
    // const g =
    //   '//feedback.cnbl.io/?p=526&ph_apikey=27351b29a4c63f1b37aa8492cac493f3';
    // let t = new URLSearchParams(window.location.search).get(k);
    // if (t) localStorage.setItem(k, t);
    // t = localStorage.getItem(k) || '';
    // const script = document.createElement('script');
    // script.type = 'text/javascript';
    // script.async = true;
    // script.defer = true;
    // script.charset = 'UTF-8';
    // script.src =
    //   g + '&v=' + new Date().getTime() + (t ? '&' + k + '=' + t : '');
    // document.head.appendChild(script);

    const checkReleaseNotesHash = () => {
      const hash = window.location.hash?.toLowerCase();
      if (hash === '#new' || hash === '#release-notes') {
        this.auth.user$.pipe(take(1)).subscribe((user) => {
          if (user) {
            this.announcementService.openModal();
          } else {
            // Not logged in: set flag and redirect to login
            localStorage.setItem('showReleaseNotesAfterLogin', '1');
            this.router.navigate(['/login']);
          }
        });
      }
    };
    checkReleaseNotesHash();
    window.addEventListener('hashchange', checkReleaseNotesHash);

    // --- Version Check for Cache Busting ---
    fetch('/assets/version.json', { cache: 'no-store' })
      .then((res) => res.json())
      .then((data) => {
        this.latestVersion = data.version;
        // @ts-ignore
        if (this.latestVersion && this.latestVersion !== environment.version) {
          this.showUpdateBanner = true;
        }
      })
      .catch(() => {});
  }
}
